/**
 * Core pixel event mapping, parameter transformation, and event gating.
 *
 * This module is framework-agnostic (no React, no DOM) so it can be shared
 * between the React `PixelTrackingProvider` and the standalone vanilla SDK.
 *
 * Official event name references (verified Feb 2026 against vendor docs):
 *   Meta:      fbq('track', 'Purchase', { value, currency })       — developers.facebook.com/docs/meta-pixel/reference
 *   TikTok:    ttq.track('Purchase', { value, currency })          — ads.tiktok.com/help/article/standard-events-parameters (Sep 2025)
 *   Snapchat:  snaptr('track', 'PURCHASE', { price, currency })    — developers.snap.com/api/marketing-api/Conversions-API/Parameters
 *   Pinterest: pintrk('track', 'checkout', { value, currency })    — help.pinterest.com/en/business/article/add-event-codes
 *   GTM/GA4:   gtag('event', 'purchase', { value, currency, items })
 *   Clarity:   clarity('event', 'Purchase')                        — learn.microsoft.com/en-us/clarity/setup-and-installation/clarity-api
 *   AppLovin:  axon('track', 'purchase', { value, currency, items }) — support.axon.ai/en/growth/axon-pixel/events-and-objects
 */

import type {
  PixelTrackingConfig,
  SnapchatTrackingConfig,
  GTMTrackingConfig,
  ClarityTrackingConfig,
  ApplovinTrackingConfig,
  PixelConfig,
  PixelsConfig,
} from './funnelClient';
import type { PixelRawIdentity } from './pixelIdentity';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type StandardPixelEvent =
  | 'PageView'
  | 'ViewContent'
  | 'AddToCart'
  | 'AddToWishlist'
  | 'Search'
  | 'InitiateCheckout'
  | 'AddPaymentInfo'
  | 'Purchase'
  | 'Lead'
  | 'CompleteRegistration'
  | 'Conversion';

export type PixelProviderKey = 'facebook' | 'tiktok' | 'snapchat' | 'pinterest' | 'gtm' | 'clarity' | 'applovin';

/** The field a pixel entry carries its tracking id in. */
export type PixelIdKey = 'pixelId' | 'containerId' | 'projectId' | 'eventKey';

/**
 * Which key holds this entry's id: GTM stores a `containerId`, Clarity a
 * `projectId`, AppLovin an `eventKey`, every other provider a `pixelId`.
 * Callers that sanitize, split or display an id go through here rather than
 * testing the keys themselves, so a new provider with its own field name is
 * one edit, not six.
 */
export function pixelIdKeyOf(pixel: unknown): PixelIdKey {
  if (pixel && typeof pixel === 'object') {
    if ('containerId' in pixel) return 'containerId';
    if ('projectId' in pixel) return 'projectId';
    if ('eventKey' in pixel) return 'eventKey';
  }
  return 'pixelId';
}

export interface MappedEvent {
  name: string;
  params: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// GA4-only enrichment
//
// Canonical GA4 ecommerce `purchase` data carries user identity + a nested
// `ecommerce` object that the flat Meta/TikTok payload does not. We attach it
// to the shared track() params under the reserved `_ga4` key. ONLY mapGTMEvent
// reads it; `baseTransform` strips it so it can never leak into the
// Meta/TikTok/Snapchat/Pinterest payloads (those would otherwise spread it,
// shipping unhashed PII to those pixels). All monetary fields are in MINOR
// units here — mapGTMEvent converts to major via the same currency helpers.
// ---------------------------------------------------------------------------

export const GA4_PARAM_KEY = '_ga4' as const;

export interface Ga4UserData {
  email_address?: string;
  phone_number?: string;
  address?: {
    first_name?: string;
    last_name?: string;
    street?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
}

export interface Ga4Item {
  item_id?: string;
  item_name?: string;
  item_variant?: string;
  /** Minor units (cents). Post-discount unit price (GA4 spec). Converted to major by mapGTMEvent. */
  price?: number;
  quantity?: number;
  /** Minor units (cents). Per-unit monetary discount (line-level + allocated order-level). */
  discount?: number;
  /** Coupon code(s) that discounted this item. */
  coupon?: string;
}

export interface Ga4PurchasePayload {
  user_id?: string;
  user_data?: Ga4UserData;
  /** Minor units (cents). */
  value?: number;
  /** Minor units (cents). */
  tax?: number;
  /** Minor units (cents). */
  shipping?: number;
  /** Order-level coupon code(s), comma-joined. */
  coupon?: string;
  items: Ga4Item[];
}

/** Loose order shape accepted by buildGa4PurchasePayload (subset of SDK Order). */
export interface Ga4OrderInput {
  id?: string;
  currency?: string;
  paidAmount?: number;
  items?: Array<{
    productId?: string;
    variantId?: string | null;
    quantity?: number;
    /** Pre-discount unit amount (minor units). */
    amount?: number;
    adjustedAmount?: number;
    orderLineItemProduct?: { name?: string };
    orderLineItemVariant?: { name?: string };
  }>;
  summaries?: Array<{
    currency?: string;
    totalTaxAmount?: number;
    shippingCost?: number;
    totalAdjustedAmount?: number;
    totalPromotionAmount?: number;
  }>;
  /**
   * Applied promotions. Accepts both shapes seen in the wild:
   * promotion-usage rows with a nested `promotion` (CMS order endpoint) and
   * flat promotion summaries with a `code` (SDK OrderWithRelations).
   */
  promotions?: Array<{
    code?: string | null;
    promotion?: { code?: string | null } | null;
  }>;
  customer?: { id?: string; email?: string; phone?: string; firstName?: string; lastName?: string };
  billingAddress?: Ga4Address;
  shippingAddress?: Ga4Address;
}

/**
 * Extract the distinct coupon codes applied to an order, comma-joined.
 * Returns undefined when the order carries no coded promotions (automatic
 * promotions have `code: null` and are deliberately excluded — GA4's `coupon`
 * dimension is for codes the shopper entered).
 */
export function extractCouponCodes(order: Pick<Ga4OrderInput, 'promotions'>): string | undefined {
  const codes = (order.promotions ?? [])
    .map((p) => p.code ?? p.promotion?.code)
    .filter((c): c is string => typeof c === 'string' && c.length > 0);
  if (codes.length === 0) return undefined;
  return [...new Set(codes)].join(',');
}

interface Ga4Address {
  firstName?: string;
  lastName?: string;
  address1?: string;
  city?: string;
  state?: string;
  postal?: string;
  country?: string;
  phone?: string;
}

/**
 * Build the GA4-only enrichment payload (`_ga4`) from an order. Pure, defensive,
 * framework-agnostic — shared by the native ThankYouPage and the Studio
 * OrderConfirmation island so both emit an identical canonical GA4 purchase.
 * Returns minor-unit amounts; mapGTMEvent converts them to major units.
 */
export function buildGa4PurchasePayload(order: Ga4OrderInput): Ga4PurchasePayload {
  const sourceItems = order.items ?? [];
  const coupon = extractCouponCodes(order);

  const summary =
    order.summaries?.find((s) => s.currency === order.currency) ?? order.summaries?.[0];

  const itemsSum = sourceItems.reduce(
    (sum, i) => sum + (i.adjustedAmount ?? 0) * (i.quantity ?? 0),
    0,
  );

  // GA4 requires order-level discounts to be allocated across items: `price`
  // must be the post-discount unit price and `discount` the per-unit monetary
  // discount. Line-level discounts are already baked into adjustedAmount; the
  // order-level remainder (totalPromotionAmount minus line-level discounts,
  // capped to the item subtotal — promos can also cover shipping) is spread
  // proportionally by line value.
  const lineLevelDiscountTotal = sourceItems.reduce(
    (sum, i) => sum + Math.max(0, (i.amount ?? i.adjustedAmount ?? 0) - (i.adjustedAmount ?? 0)) * (i.quantity ?? 0),
    0,
  );
  const orderLevelDiscount = Math.min(
    Math.max(0, (summary?.totalPromotionAmount ?? 0) - lineLevelDiscountTotal),
    itemsSum,
  );

  let remainingAllocation = orderLevelDiscount;
  const items: Ga4Item[] = sourceItems.map((item, index) => {
    const quantity = item.quantity ?? 0;
    const unitAdjusted = item.adjustedAmount ?? 0;
    const lineValue = unitAdjusted * quantity;

    // Last item takes the remainder so allocations sum exactly.
    const lineAllocation =
      orderLevelDiscount === 0 || itemsSum === 0
        ? 0
        : index === sourceItems.length - 1
          ? remainingAllocation
          : Math.round((orderLevelDiscount * lineValue) / itemsSum);
    remainingAllocation -= lineAllocation;

    const unitAllocation = quantity > 0 ? lineAllocation / quantity : 0;
    const lineLevelUnitDiscount = Math.max(0, (item.amount ?? unitAdjusted) - unitAdjusted);
    const unitDiscount = lineLevelUnitDiscount + unitAllocation;

    const ga4Item: Ga4Item = {
      item_id: item.productId || undefined,
      // GA4 convention: item_name = product, item_variant = variant.
      item_name: item.orderLineItemProduct?.name || item.orderLineItemVariant?.name || undefined,
      item_variant: item.orderLineItemVariant?.name || undefined,
      price: unitAdjusted - unitAllocation,
      quantity: item.quantity,
    };
    if (unitDiscount > 0) {
      ga4Item.discount = unitDiscount;
      if (coupon) ga4Item.coupon = coupon;
    }
    return ga4Item;
  });

  // Prefer the captured paid amount; fall back to the summary total, then to
  // the sum of line items. Guards against the value:0 seen in the field.
  const value =
    order.paidAmount && order.paidAmount > 0
      ? order.paidAmount
      : (summary?.totalAdjustedAmount ?? itemsSum);

  const payload: Ga4PurchasePayload = { items, value };
  if (summary?.totalTaxAmount != null) payload.tax = summary.totalTaxAmount;
  if (summary?.shippingCost != null) payload.shipping = summary.shippingCost;
  if (coupon) payload.coupon = coupon;

  if (order.customer?.id) payload.user_id = order.customer.id;

  const addr = order.billingAddress ?? order.shippingAddress;
  const userData: Ga4UserData = {};
  if (order.customer?.email) userData.email_address = order.customer.email;
  const phone = order.customer?.phone || addr?.phone;
  if (phone) userData.phone_number = phone;
  if (addr) {
    const address = {
      first_name: addr.firstName || order.customer?.firstName || undefined,
      last_name: addr.lastName || order.customer?.lastName || undefined,
      street: addr.address1 || undefined,
      city: addr.city || undefined,
      region: addr.state || undefined,
      postal_code: addr.postal || undefined,
      country: addr.country || undefined,
    };
    if (Object.values(address).some((v) => v != null)) userData.address = address;
  }
  if (Object.keys(userData).length > 0) payload.user_data = userData;

  return payload;
}

// ---------------------------------------------------------------------------
// Currency conversion helper (inline, zero-dep)
// ---------------------------------------------------------------------------

/**
 * Convert minor units (cents) to major units using ISO 4217 digit counts.
 * Falls back to ÷100 for unknown currencies.
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
]);

const THREE_DECIMAL_CURRENCIES = new Set(['BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND']);

function minorToMajor(amount: number, currency: string): number {
  const code = currency.toUpperCase();
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return amount;
  if (THREE_DECIMAL_CURRENCIES.has(code)) return amount / 1000;
  return amount / 100;
}

// ---------------------------------------------------------------------------
// Event gating – respects per-event toggles
// ---------------------------------------------------------------------------

/**
 * Returns `true` if the given event is enabled on the pixel config.
 * If the pixel has no `events` map (e.g. MetaConversionTrackingConfig) we allow all.
 */
export function isEventEnabled(pixel: PixelConfig, eventName: StandardPixelEvent): boolean {
  if (!('events' in pixel) || !pixel.events) return true;
  const events = pixel.events as Record<string, boolean>;
  if (!(eventName in events)) return true;   // unknown event → allow
  return events[eventName] === true;
}

/**
 * Filter an array of pixel configs to only those that are globally enabled
 * AND have the specific event toggled on.
 */
export function getEligiblePixels<T extends PixelConfig>(
  pixels: T[] | undefined,
  eventName: StandardPixelEvent,
): T[] {
  if (!pixels) return [];
  return pixels.filter((p) => p.enabled && isEventEnabled(p, eventName));
}

// ---------------------------------------------------------------------------
// Shared parameter transforms
// ---------------------------------------------------------------------------

function ensureCurrencyUppercase(params: Record<string, unknown>): Record<string, unknown> {
  if (params.currency && typeof params.currency === 'string') {
    return { ...params, currency: params.currency.toUpperCase() };
  }
  return { ...params };
}

function convertValueToMajor(params: Record<string, unknown>): Record<string, unknown> {
  if (params.currency && params.value != null) {
    const currency = String(params.currency);
    const major = minorToMajor(Number(params.value), currency);
    return { ...params, value: major };
  }
  return params;
}

/** Convert top-level monetary fields (shipping, tax, subtotal, total_discount) from minor → major units. */
function convertMonetaryFieldsToMajor(params: Record<string, unknown>): Record<string, unknown> {
  const currency = params.currency ? String(params.currency) : null;
  if (!currency) return params;
  const result = { ...params };
  for (const field of ['shipping', 'tax', 'subtotal', 'total_discount'] as const) {
    if (result[field] != null) {
      result[field] = minorToMajor(Number(result[field]), currency);
    }
  }
  return result;
}

/** Convert price fields inside contents[] items from minor → major units. */
function convertContentsPricesToMajor(params: Record<string, unknown>): Record<string, unknown> {
  if (!params.contents || !Array.isArray(params.contents)) return params;
  const currency = params.currency ? String(params.currency) : null;
  const result = { ...params };
  result.contents = (params.contents as Record<string, unknown>[]).map((item) => {
    const itemCurrency = item.currency ? String(item.currency) : currency;
    if (!itemCurrency) return { ...item };
    const converted = { ...item };
    if (converted.price != null) converted.price = minorToMajor(Number(converted.price), itemCurrency);
    if (converted.original_price != null) converted.original_price = minorToMajor(Number(converted.original_price), itemCurrency);
    return converted;
  });
  return result;
}

function baseTransform(params: Record<string, unknown>): Record<string, unknown> {
  let p = ensureCurrencyUppercase(params);
  p = convertValueToMajor(p);
  p = convertMonetaryFieldsToMajor(p);
  p = convertContentsPricesToMajor(p);
  // Strip GA4-only enrichment so it never reaches Meta/TikTok/Snapchat/Pinterest
  // (those mappers spread all params). mapGTMEvent reads it from the raw input.
  if (GA4_PARAM_KEY in p) {
    p = { ...p };
    delete p[GA4_PARAM_KEY];
  }
  return p;
}

// ---------------------------------------------------------------------------
// Meta / Facebook
// ---------------------------------------------------------------------------

const META_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'PageView',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  AddToWishlist: 'AddToWishlist',
  Search: 'Search',
  InitiateCheckout: 'InitiateCheckout',
  AddPaymentInfo: 'AddPaymentInfo',
  Purchase: 'Purchase',
  Lead: 'Lead',
  CompleteRegistration: 'CompleteRegistration',
};

export function mapMetaEvent(eventName: StandardPixelEvent, parameters: Record<string, unknown>): MappedEvent {
  const name = META_EVENT_MAP[eventName] ?? eventName;
  // `coupon` intentionally flows through: Meta has no standard coupon param but
  // accepts custom parameters on standard events (usable in custom conversions).
  const params = baseTransform(parameters);

  if (params.contents && Array.isArray(params.contents)) {
    params.content_ids = (params.contents as Record<string, unknown>[]).map((i) => i.content_id).filter(Boolean);
    params.content_type = params.content_type ?? 'product';

    // Reshape contents to Meta's expected format (prices already converted by baseTransform)
    params.contents = (params.contents as Record<string, unknown>[]).map((item) => ({
      id: item.content_id,
      quantity: item.quantity != null ? Number(item.quantity) : undefined,
      item_price: item.price != null ? Number(item.price) : undefined,
      content_name: item.content_name,
      content_category: item.content_category,
    }));
  }

  return { name, params };
}

// ---------------------------------------------------------------------------
// TikTok
// Official: ttq.track('Purchase', { value, currency })
// Source: ads.tiktok.com/help/article/standard-events-parameters (Sep 2025)
// Standard events use PascalCase. Note: TikTok has no standard PageView event
// (page views use ttq.page()), so we pass it as-is for custom tracking.
// TikTok has no Lead event; SubmitForm is the closest standard equivalent.
// ---------------------------------------------------------------------------

const TIKTOK_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'Pageview',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  AddToWishlist: 'AddToWishlist',
  Search: 'Search',
  InitiateCheckout: 'InitiateCheckout',
  AddPaymentInfo: 'AddPaymentInfo',
  Purchase: 'Purchase',
  Lead: 'SubmitForm',
  CompleteRegistration: 'CompleteRegistration',
};

export function mapTikTokEvent(eventName: StandardPixelEvent, parameters: Record<string, unknown>): MappedEvent {
  const name = TIKTOK_EVENT_MAP[eventName] ?? eventName;
  const params = baseTransform(parameters);

  // TikTok's event spec has no coupon/discount parameter — strip it to keep
  // the payload within spec (avoids Events Manager diagnostics warnings).
  delete params.coupon;

  if (params.contents && Array.isArray(params.contents)) {
    const ids = (params.contents as Record<string, unknown>[])
      .map((i) => i.content_id)
      .filter(Boolean) as string[];

    // TikTok requires content_id (singular) at the top level for VSA
    if (!params.content_id && ids.length > 0) {
      params.content_id = ids.length === 1 ? ids[0] : JSON.stringify(ids);
    }

    // Also keep content_ids for backwards compatibility
    if (!params.content_ids) {
      params.content_ids = ids;
    }

    // Reshape contents to TikTok's expected format (prices already converted by baseTransform)
    params.contents = (params.contents as Record<string, unknown>[]).map((item) => ({
      content_id: item.content_id,
      content_name: item.content_name,
      content_type: item.content_type ?? 'product',
      content_category: item.content_category,
      price: item.price != null ? Number(item.price) : undefined,
      original_price: item.original_price != null ? Number(item.original_price) : undefined,
      quantity: item.quantity != null ? Number(item.quantity) : undefined,
      description: item.description,
    }));
  }

  // Set required fields for InitiateCheckout
  if (eventName === 'InitiateCheckout') {
    params.content_type = params.content_type ?? 'product';
    if (!params.delivery_category) {
      params.delivery_category = 'home_delivery';
    }
  }

  return { name, params };
}

// ---------------------------------------------------------------------------
// Snapchat
// Official: snaptr('track', 'PURCHASE', { price, currency })
// Source: developers.snap.com/api/marketing-api/Conversions-API/Parameters
// All event names are UPPER_SNAKE_CASE.
// Note: Snapchat has no LEAD standard event; we pass it as-is (custom event).
// ---------------------------------------------------------------------------

const SNAPCHAT_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'PAGE_VIEW',
  ViewContent: 'VIEW_CONTENT',
  AddToCart: 'ADD_CART',
  AddToWishlist: 'ADD_TO_WISHLIST',
  Search: 'SEARCH',
  InitiateCheckout: 'START_CHECKOUT',
  AddPaymentInfo: 'ADD_BILLING',
  Purchase: 'PURCHASE',
  Lead: 'LEAD',
  CompleteRegistration: 'SIGN_UP',
};

export function mapSnapchatEvent(eventName: StandardPixelEvent, parameters: Record<string, unknown>): MappedEvent {
  const name = SNAPCHAT_EVENT_MAP[eventName] ?? eventName;
  let params = baseTransform(parameters);

  // Snap's parameter spec has no coupon/discount field — strip it.
  delete params.coupon;

  // Snapchat uses `price` not `value`
  if ('value' in params) {
    params = { ...params, price: params.value };
    delete params.value;
  }

  // Snapchat uses `number_items` not `num_items`
  if ('num_items' in params) {
    params = { ...params, number_items: params.num_items };
    delete params.num_items;
  }

  if (params.contents && Array.isArray(params.contents)) {
    params.item_ids = (params.contents as { content_id?: string }[]).map((i) => i.content_id).filter(Boolean);
  }

  return { name, params };
}

// ---------------------------------------------------------------------------
// Pinterest
// Official: pintrk('track', 'checkout', { value, currency, line_items })
// Event names are all lowercase.
// ---------------------------------------------------------------------------

const PINTEREST_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'pagevisit',
  ViewContent: 'viewcontent',
  AddToCart: 'addtocart',
  AddToWishlist: 'addtowishlist',
  Search: 'search',
  InitiateCheckout: 'initiatecheckout',
  AddPaymentInfo: 'addpaymentinfo',
  Purchase: 'checkout',
  Lead: 'lead',
  CompleteRegistration: 'signup',
};

export function mapPinterestEvent(eventName: StandardPixelEvent, parameters: Record<string, unknown>): MappedEvent {
  const name = PINTEREST_EVENT_MAP[eventName] ?? eventName.toLowerCase();
  const params = baseTransform(parameters);

  // Pinterest's official field for coupon codes is `promo_code`.
  if (params.coupon != null) {
    params.promo_code = params.coupon;
    delete params.coupon;
  }

  if (params.order_quantity !== undefined) {
    params.order_quantity = Number(params.order_quantity);
  }

  if (params.contents && Array.isArray(params.contents)) {
    // Reshape to Pinterest line_items format (prices already converted by baseTransform)
    params.line_items = (params.contents as Record<string, unknown>[]).map((item) => ({
      product_id: item.content_id,
      product_name: item.content_name,
      product_category: item.content_category,
      product_price: item.price != null ? Number(item.price) : undefined,
      product_quantity: item.quantity != null ? Number(item.quantity) : undefined,
    }));
  }

  return { name, params };
}

// ---------------------------------------------------------------------------
// GTM / GA4 / Google Ads
// Official: gtag('event', 'purchase', { value, currency, items })
// Event names are snake_case (GA4 convention).
// ---------------------------------------------------------------------------

const GTM_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'page_view',
  ViewContent: 'view_item',
  AddToCart: 'add_to_cart',
  AddToWishlist: 'add_to_wishlist',
  Search: 'search',
  InitiateCheckout: 'begin_checkout',
  AddPaymentInfo: 'add_payment_info',
  Purchase: 'purchase',
  Lead: 'generate_lead',
  CompleteRegistration: 'sign_up',
  Conversion: 'conversion',
};

export function mapGTMEvent(eventName: StandardPixelEvent, parameters: Record<string, unknown>): MappedEvent {
  const name = GTM_EVENT_MAP[eventName] ?? eventName.toLowerCase();

  // Canonical GA4 ecommerce shape: when the caller attached `_ga4` enrichment
  // (purchase from the thank-you page) emit a nested `ecommerce` object plus
  // user identity instead of the legacy flat payload. baseTransform strips
  // `_ga4`, so read it from the raw input first.
  const ga4 = parameters[GA4_PARAM_KEY] as Ga4PurchasePayload | undefined;
  if (ga4) {
    const currency =
      typeof parameters.currency === 'string' ? parameters.currency.toUpperCase() : undefined;
    const toMajor = (v: number | undefined): number | undefined =>
      v == null || !currency ? v : minorToMajor(Number(v), currency);

    const ecommerce: Record<string, unknown> = {
      transaction_id: parameters.transaction_id,
      currency,
      value: toMajor(ga4.value),
    };
    if (ga4.tax != null) ecommerce.tax = toMajor(ga4.tax);
    if (ga4.shipping != null) ecommerce.shipping = toMajor(ga4.shipping);
    if (ga4.coupon) ecommerce.coupon = ga4.coupon;
    ecommerce.items = (ga4.items ?? []).map((item) => ({
      item_id: item.item_id,
      item_name: item.item_name,
      item_variant: item.item_variant,
      price: toMajor(item.price),
      quantity: item.quantity != null ? Number(item.quantity) : undefined,
      ...(item.discount != null ? { discount: toMajor(item.discount) } : {}),
      ...(item.coupon ? { coupon: item.coupon } : {}),
    }));

    const out: Record<string, unknown> = { ecommerce };
    if (ga4.user_id) out.user_id = ga4.user_id;
    if (ga4.user_data) out.user_data = ga4.user_data;
    return { name, params: out };
  }

  const params = baseTransform(parameters);

  if (params.num_items !== undefined) {
    params.num_items = Number(params.num_items);
  }

  if (params.contents && Array.isArray(params.contents)) {
    // Reshape to GA4 items format (prices already converted by baseTransform)
    params.items = (params.contents as Record<string, unknown>[]).map((item) => ({
      item_id: item.content_id,
      item_name: item.content_name,
      item_category: item.content_category,
      price: item.price != null ? Number(item.price) : undefined,
      quantity: item.quantity != null ? Number(item.quantity) : undefined,
    }));

    params.contents = (params.contents as Record<string, unknown>[]).map((item) => ({
      id: item.content_id,
      name: item.content_name,
      category: item.content_category,
      price: item.price != null ? Number(item.price) : undefined,
      quantity: item.quantity != null ? Number(item.quantity) : undefined,
    }));
  }

  return { name, params };
}

// ---------------------------------------------------------------------------
// Microsoft Clarity
// Official: clarity('event', 'Purchase') — custom ("smart") events take a name
// and no parameters, so the mapped params are carried for the dedup guard only.
// Clarity records page views by itself; the PageView toggle still emits a named
// event so a merchant can filter recordings on it like any other funnel step.
// ---------------------------------------------------------------------------

const CLARITY_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'PageView',
  ViewContent: 'ViewContent',
  AddToCart: 'AddToCart',
  AddToWishlist: 'AddToWishlist',
  Search: 'Search',
  InitiateCheckout: 'InitiateCheckout',
  AddPaymentInfo: 'AddPaymentInfo',
  Purchase: 'Purchase',
  Lead: 'Lead',
  CompleteRegistration: 'CompleteRegistration',
};

export function mapClarityEvent(
  eventName: StandardPixelEvent,
  parameters: Record<string, unknown>,
): MappedEvent {
  const name = CLARITY_EVENT_MAP[eventName] ?? eventName;
  return { name, params: baseTransform(parameters) };
}

// ---------------------------------------------------------------------------
// AppLovin (Axon pixel)
// Official: axon('track', 'purchase', { currency, items, shipping, tax,
// transaction_id, value, user_data }, { dedupe_id })
// Source: support.axon.ai/en/growth/axon-pixel/events-and-objects
// Event names are snake_case. Only the events below exist for a purchase site;
// any other standard event is not sent to AppLovin. Every payload is rebuilt
// from scratch, so a parameter AppLovin does not define never reaches it.
// ---------------------------------------------------------------------------

export const APPLOVIN_EVENT_MAP: Partial<Record<StandardPixelEvent, string>> = {
  PageView: 'page_view',
  ViewContent: 'view_item',
  AddToCart: 'add_to_cart',
  InitiateCheckout: 'begin_checkout',
  AddPaymentInfo: 'add_payment_info',
  Purchase: 'purchase',
};

/** AppLovin's user data object. Raw values: the Axon script hashes them itself. */
export interface ApplovinUserData {
  email?: string;
  phone?: string;
  user_id?: string;
}

const APPLOVIN_MIN_USER_ID_LENGTH = 6;
const APPLOVIN_IMAGE_PATTERN = /\.(png|jpe?g)$/i;
const MASKED_VALUE_PATTERN = /[*•]/;

/**
 * Shape an identity as AppLovin's `user_data`: email lowercased, phone as
 * digits only (leading zeros dropped, as AppLovin's normalisation does), and
 * the external id as `user_id` only when it has the six characters AppLovin
 * requires. A masked email or phone (`r***@gmail.com`, as some order reads
 * return it) is dropped: AppLovin would hash it into a key that matches no one.
 * Returns undefined when nothing usable is left.
 */
export function toApplovinUserData(identity: PixelRawIdentity): ApplovinUserData | undefined {
  const out: ApplovinUserData = {};
  const email = identity.email?.trim().toLowerCase();
  if (email && !MASKED_VALUE_PATTERN.test(email)) out.email = email;
  const rawPhone = identity.phone?.trim();
  const phone = rawPhone && !MASKED_VALUE_PATTERN.test(rawPhone) && !/[a-z]/i.test(rawPhone)
    ? rawPhone.replace(/[^0-9]/g, '').replace(/^0+/, '')
    : undefined;
  if (phone) out.phone = phone;
  const userId = identity.externalId?.trim();
  if (userId && userId.length >= APPLOVIN_MIN_USER_ID_LENGTH) out.user_id = userId;
  return Object.keys(out).length > 0 ? out : undefined;
}

/**
 * The entry a page loads: AppLovin's script takes one event key per page, so
 * only the first enabled entry is initialised and only its toggles apply.
 */
export function activeApplovinPixel(
  pixels: ApplovinTrackingConfig[] | undefined,
): ApplovinTrackingConfig | undefined {
  return pixels?.find((pixel) => pixel.enabled);
}

function finiteNumber(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function roundMoney(amount: number): number {
  return Math.round(amount * 1000) / 1000;
}

/**
 * Our `content_id` is `<productId>-<variantId>`. A product id may hold dashes
 * (`bpc157-10mg`), a variant id does not: the longest known product id that
 * starts the content id wins, else the split is at the last dash.
 */
function splitContentId(contentId: string, productIds: readonly string[]): { itemId: string; variantId?: string } {
  const productId = productIds
    .filter((id) => contentId.startsWith(`${id}-`))
    .reduce<string | undefined>((longest, id) => (!longest || id.length > longest.length ? id : longest), undefined);
  if (productId) {
    const variantId = contentId.slice(productId.length + 1);
    return variantId ? { itemId: productId, variantId } : { itemId: productId };
  }
  const dash = contentId.lastIndexOf('-');
  if (dash === -1) return { itemId: contentId };
  const product = contentId.slice(0, dash);
  const variant = contentId.slice(dash + 1);
  if (!product) return { itemId: variant };
  return variant ? { itemId: product, variantId: variant } : { itemId: product };
}

function applovinImageUrl(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value) return undefined;
  const path = value.split(/[?#]/)[0];
  return APPLOVIN_IMAGE_PATTERN.test(path) ? value : undefined;
}

/**
 * Reads `contents[]` after baseTransform, so `price` is already in major units.
 * `productIds` are the order's own product ids, from its GA4 items.
 */
function applovinItems(contents: unknown, productIds: readonly string[]): Record<string, unknown>[] {
  if (!Array.isArray(contents)) return [];
  return contents.flatMap((entry) => {
    if (!entry || typeof entry !== 'object') return [];
    const item = entry as Record<string, unknown>;
    const rawId = typeof item.content_id === 'string' && item.content_id ? item.content_id : item.id;
    if (typeof rawId !== 'string' || !rawId) return [];
    const { itemId, variantId } = splitContentId(rawId, productIds);
    if (!itemId) return [];
    const out: Record<string, unknown> = { item_id: itemId };
    if (variantId) out.item_variant_id = variantId;
    if (typeof item.content_name === 'string' && item.content_name) out.item_name = item.content_name;
    const price = finiteNumber(item.price);
    if (price != null) out.price = price;
    const quantity = finiteNumber(item.quantity);
    if (quantity != null) out.quantity = quantity;
    const imageUrl = applovinImageUrl(item.image_url);
    if (imageUrl) out.image_url = imageUrl;
    return [out];
  });
}

/**
 * Map a standard event to AppLovin's name and payload, or null when AppLovin
 * has no such event. Amounts leave in major units. `value` is the subtotal
 * AppLovin asks for on `begin_checkout` and `purchase`: the amount minus
 * shipping and tax, never below zero.
 */
export function mapApplovinEvent(
  eventName: StandardPixelEvent,
  parameters: Record<string, unknown>,
): MappedEvent | null {
  const name = APPLOVIN_EVENT_MAP[eventName];
  if (!name) return null;

  const ga4 = parameters[GA4_PARAM_KEY] as Ga4PurchasePayload | undefined;
  const params: Record<string, unknown> = {};
  const userData = ga4
    ? toApplovinUserData({
        email: ga4.user_data?.email_address,
        phone: ga4.user_data?.phone_number,
        externalId: ga4.user_id,
      })
    : undefined;

  if (eventName === 'PageView') {
    if (userData) params.user_data = userData;
    return { name, params };
  }

  const p = baseTransform(parameters);
  const currency = typeof p.currency === 'string' && p.currency ? p.currency : undefined;
  const toMajor = (amount: unknown): number | undefined => {
    const n = finiteNumber(amount);
    return n == null || !currency ? undefined : minorToMajor(n, currency);
  };

  if (currency) params.currency = currency;
  const productIds = (ga4?.items ?? [])
    .map((item) => item.item_id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0);
  const items = applovinItems(p.contents, productIds);
  if (items.length > 0) params.items = items;

  // Without a currency `value` is still in minor units, so it is left out.
  const value = currency ? finiteNumber(p.value) ?? toMajor(ga4?.value) : undefined;

  if (eventName === 'InitiateCheckout' || eventName === 'Purchase') {
    const shipping = (currency ? finiteNumber(p.shipping) : undefined) ?? toMajor(ga4?.shipping) ?? 0;
    const tax = (currency ? finiteNumber(p.tax) : undefined) ?? toMajor(ga4?.tax) ?? 0;
    if (value != null) params.value = Math.max(0, roundMoney(value - shipping - tax));
    if (eventName === 'Purchase') {
      params.shipping = shipping;
      params.tax = tax;
      if (p.transaction_id != null && p.transaction_id !== '') params.transaction_id = String(p.transaction_id);
    }
  } else if (value != null) {
    params.value = value;
  }

  if (userData) params.user_data = userData;
  return { name, params };
}

function googleAdsTag(id: unknown): string {
  const text = String(id).trim();
  return /^\d+$/.test(text) ? `AW-${text}` : text;
}

/**
 * Append Google Ads `send_to` parameter when conversion tracking is configured.
 */
export function applyGoogleAdsConversion(
  params: Record<string, unknown>,
  eventName: StandardPixelEvent,
  pixel: GTMTrackingConfig,
): Record<string, unknown> {
  if (
    (eventName === 'Purchase' || eventName === 'Conversion') &&
    pixel.googleAdsConversionId &&
    pixel.googleAdsConversionLabel
  ) {
    // Merchants paste the bare number Google Ads shows; `send_to` wants it as `AW-<number>`.
    return {
      ...params,
      send_to: `${googleAdsTag(pixel.googleAdsConversionId)}/${pixel.googleAdsConversionLabel}`,
    };
  }
  return params;
}

// ---------------------------------------------------------------------------
// Dispatcher – maps a StandardPixelEvent to all providers in one call
// ---------------------------------------------------------------------------

export interface ProviderEvent {
  provider: PixelProviderKey;
  mapped: MappedEvent;
  pixel: PixelConfig;
}

/**
 * Given a PixelsConfig and an event, return per-provider mapped events
 * for every eligible (enabled + event-toggled-on) pixel.
 */
export function resolvePixelEvents(
  pixels: PixelsConfig,
  eventName: StandardPixelEvent,
  parameters: Record<string, unknown>,
): ProviderEvent[] {
  const results: ProviderEvent[] = [];

  const fb = getEligiblePixels(pixels.facebook, eventName);
  if (fb.length > 0) {
    const mapped = mapMetaEvent(eventName, parameters);
    fb.forEach((pixel) => results.push({ provider: 'facebook', mapped, pixel }));
  }

  const tt = getEligiblePixels(pixels.tiktok, eventName);
  if (tt.length > 0) {
    const mapped = mapTikTokEvent(eventName, parameters);
    tt.forEach((pixel) => results.push({ provider: 'tiktok', mapped, pixel }));
  }

  const sc = getEligiblePixels(pixels.snapchat as SnapchatTrackingConfig[] | undefined, eventName);
  if (sc.length > 0) {
    const mapped = mapSnapchatEvent(eventName, parameters);
    sc.forEach((pixel) => results.push({ provider: 'snapchat', mapped, pixel }));
  }

  const pt = getEligiblePixels(pixels.pinterest, eventName);
  if (pt.length > 0) {
    const mapped = mapPinterestEvent(eventName, parameters);
    pt.forEach((pixel) => results.push({ provider: 'pinterest', mapped, pixel }));
  }

  const clarity = getEligiblePixels(pixels.clarity as ClarityTrackingConfig[] | undefined, eventName);
  if (clarity.length > 0) {
    const mapped = mapClarityEvent(eventName, parameters);
    clarity.forEach((pixel) => results.push({ provider: 'clarity', mapped, pixel }));
  }

  const applovin = activeApplovinPixel(pixels.applovin);
  if (applovin && getEligiblePixels([applovin], eventName).length > 0) {
    const mapped = mapApplovinEvent(eventName, parameters);
    if (mapped) results.push({ provider: 'applovin', mapped, pixel: applovin });
  }

  const gtm = getEligiblePixels(pixels.gtm as GTMTrackingConfig[] | undefined, eventName);
  if (gtm.length > 0) {
    const mapped = mapGTMEvent(eventName, parameters);
    gtm.forEach((pixel) => {
      const finalParams = applyGoogleAdsConversion(mapped.params, eventName, pixel as GTMTrackingConfig);
      results.push({ provider: 'gtm', mapped: { name: mapped.name, params: finalParams }, pixel });
    });
  }

  return results;
}
